import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PageSectionProps {
  id?: string;
  num: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  right?: React.ReactNode;
  tone?: "primary" | "success" | "secondary" | "blue" | "green";
  className?: string;
  children: React.ReactNode;
}

const TONE_STYLES: Record<NonNullable<PageSectionProps["tone"]>, string> = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/10 text-success",
  secondary: "bg-secondary/10 text-secondary",
  blue: "bg-brand-blue/10 text-brand-blue",
  green: "bg-brand-green/10 text-brand-green",
};

export function PageSection({
  id,
  num,
  icon: Icon,
  title,
  subtitle,
  right,
  tone = "primary",
  className,
  children,
}: PageSectionProps) {
  return (
    <section
      id={id}
      className={cn("space-y-6 scroll-mt-14", className)}
    >
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-4 border-b border-border">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0",
                TONE_STYLES[tone],
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-foreground tracking-tight leading-tight">
              {title}
            </h2>
          </div>
          <p className="text-[14px] text-foreground-secondary leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </header>
      {children}
    </section>
  );
}
